// Chat input with file validation and KayQ branding - 110 lines ✅
import { useState, useRef, KeyboardEvent } from 'react'
import { Send, Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSendMessage: (message: string, files?: File[]) => void
  disabled?: boolean
  placeholder?: string
  allowFileUpload?: boolean
  className?: string
}

const ALLOWED_FILE_TYPES = [
  'image/png', 'image/jpeg', 'image/jpg',
  'text/plain', 'text/markdown',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-outlook',
  'application/octet-stream', // .msg files often report this MIME
]
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

export function ChatInput({
  onSendMessage,
  disabled = false,
  placeholder = 'Type your message...',
  allowFileUpload = false,
  className,
}: ChatInputProps): JSX.Element {
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSend = (): void => {
    if (message.trim() || files.length > 0) {
      onSendMessage(message.trim(), files)
      setMessage('')
      setFiles([])
      setFileError(null)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFileError(null)
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.txt', '.md', '.pdf', '.docx', '.msg']
      const invalidFiles = selectedFiles.filter((file) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase()
        const typeOk = ALLOWED_FILE_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext)
        return !typeOk || file.size > MAX_FILE_SIZE
      })

      if (invalidFiles.length > 0) {
        setFileError(
          `Invalid files: Allowed types are PNG, JPG, TXT, MD, PDF, DOCX, MSG (max 50MB)`
        )
        return
      }

      setFiles(selectedFiles)
    }
  }

  const removeFile = (index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setFileError(null)
  }

  return (
    <div className={cn('flex flex-col gap-2 border-t border-kaycoo-white-3 bg-kaycoo-white p-4', className)}>
      {fileError && (
        <div className="rounded-md bg-kaycoo-burgundy-pale px-3 py-2 text-sm text-kaycoo-burgundy-dark">
          {fileError}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 rounded-md bg-kaycoo-teal-pale px-3 py-1 text-sm text-kaycoo-teal-dark"
            >
              <Paperclip className="h-3 w-3" />
              <span className="max-w-[200px] truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="ml-1 hover:text-kaycoo-burgundy"
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="min-h-[60px] resize-none border-kaycoo-white-3 bg-white text-kaycoo-charcoal"
          aria-label="Chat message input"
        />
        <div className="flex flex-col gap-2">
          {allowFileUpload && (
            <>
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
                aria-label="Attach file"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,.png,.jpg,.jpeg,text/plain,text/markdown,.txt,.md,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.ms-outlook,.msg"
                className="hidden"
                onChange={handleFileSelect}
                aria-label="File upload input"
              />
            </>
          )}
          <Button
            onClick={handleSend}
            disabled={disabled || (!message.trim() && files.length === 0)}
            size="icon"
            className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
